import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdNotificationsActive, MdRestartAlt, MdSave } from 'react-icons/md';
import {
  AppCard,
  AppInput,
  AppTextarea,
  OutlineButton,
  PageLoader,
  PrimaryButton,
  StatusAlertCard,
} from '../../../components/ui';
import {
  isConfigFieldVisible,
  SYSTEM_CONFIG_DEFS,
  validateConfigValue,
} from '../../../data/system/systemConfig';
import {
  getSystemConfig,
  resetSystemConfig,
  updateSystemConfig,
} from '../../../repositories/systemConfig.repository';
import {
  enableAndRegisterWebPush,
  getPushStatus,
  sendTestNotification,
} from '../../../repositories/deviceTokens.repository';
import { logCurrentStaffActivity } from '../../../utils/activityLogHelper';
import { useStaffAccess } from '../../../utils/staffAccess';

function toDraft(config) {
  return SYSTEM_CONFIG_DEFS.reduce((acc, def) => {
    if (def.type === 'boolean') acc[def.key] = Boolean(config[def.key]);
    else acc[def.key] = String(config[def.key] ?? '');
    return acc;
  }, {});
}

const GROUP_ORDER = ['Vendors', 'Indents', 'General'];

export default function SystemConfigPage() {
  const toast = useToast();
  const { canEditSystemConfig } = useStaffAccess();
  const canEdit = canEditSystemConfig();
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushStatus, setPushStatus] = useState({
    configured: false,
    permission: 'default',
    hasLocalToken: false,
  });
  const [pushBusy, setPushBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testDelaySeconds, setTestDelaySeconds] = useState('0');

  const refreshPushStatus = async () => {
    try {
      const status = await getPushStatus();
      setPushStatus(status);
    } catch {
      setPushStatus({
        configured: false,
        permission: 'unsupported',
        hasLocalToken: false,
      });
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await getSystemConfig();
        if (!cancelled) setDraft(toDraft(config));
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load configuration',
            status: 'error',
            position: 'top-right',
          });
          setDraft(toDraft({}));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  useEffect(() => {
    refreshPushStatus();
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    SYSTEM_CONFIG_DEFS.forEach((def) => {
      if (!map.has(def.group)) map.set(def.group, []);
      map.get(def.group).push(def);
    });
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => [g, map.get(g)]);
  }, []);

  const patch = (key, value) => {
    setDraft((d) => {
      const next = { ...d, [key]: value };
      if (key === 'vendorSelfRegistrationEnabled' && value) {
        next.vendorSelfRegistrationDisabledReason = '';
      }
      return next;
    });
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    const nextErrors = {};
    const patchValues = {};
    SYSTEM_CONFIG_DEFS.forEach((def) => {
      const result = validateConfigValue(def.key, draft[def.key], draft);
      if (!result.ok) nextErrors[def.key] = result.error;
      else patchValues[def.key] = result.value;
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        title: 'Fix the highlighted settings',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await updateSystemConfig(patchValues);
      setDraft(toDraft(saved));
      logCurrentStaffActivity({
        actionType: 'UPDATE',
        module: 'Master Data',
        summary: 'Updated system configuration',
        entityType: 'system_config',
        entityId: 'SYSTEM',
      });
      toast({
        title: 'System configuration saved',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const reset = await resetSystemConfig();
      setDraft(toDraft(reset));
      setErrors({});
      toast({
        title: 'Reset to defaults',
        status: 'info',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to reset',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await enableAndRegisterWebPush();
      await refreshPushStatus();
      toast({
        title: 'Browser notifications enabled',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Could not enable notifications',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestNotification = async () => {
    setTestBusy(true);
    try {
      if (!pushStatus.hasLocalToken) {
        await enableAndRegisterWebPush();
        await refreshPushStatus();
      }

      // Immediate local tray ping — proves OS/Chrome permission works even if FCM is broken.
      if (Notification.permission === 'granted' && navigator.serviceWorker) {
        try {
          const reg = await navigator.serviceWorker.ready;
          await reg.showNotification('Utkarsh local test', {
            body: 'If you see this, browser notifications work. Waiting for FCM…',
            icon: '/favicon.svg',
          });
        } catch {
          // ignore
        }
      }

      const delaySec = Math.min(
        60,
        Math.max(0, Number.parseInt(testDelaySeconds, 10) || 0)
      );
      if (delaySec > 0) {
        toast({
          title: `Sending test in ${delaySec}s…`,
          status: 'info',
          position: 'top-right',
          duration: Math.min(delaySec * 1000, 5000),
        });
        await new Promise((resolve) => setTimeout(resolve, delaySec * 1000));
      }
      const data = await sendTestNotification({
        title: 'Test notification',
        body:
          delaySec > 0
            ? `Test push (delayed ${delaySec}s) from System configuration.`
            : 'This is a test push from System configuration.',
      });
      const fcm = data?.fcm;
      const tokenCount = Number(fcm?.tokenCount ?? 0);
      const successCount = Number(fcm?.successCount ?? 0);
      const errors = Array.isArray(fcm?.errors) ? fcm.errors : [];
      if (tokenCount === 0) {
        toast({
          title: 'No device token on server',
          description: 'Click Enable browser notifications again, then retry.',
          status: 'warning',
          position: 'top-right',
        });
      } else if (successCount <= 0) {
        toast({
          title: 'FCM delivery failed',
          description:
            errors[0] ||
            'Token may be invalid. Unregister SW, Enable notifications again.',
          status: 'error',
          position: 'top-right',
          duration: 9000,
        });
      } else {
        toast({
          title: 'FCM accepted by Firebase',
          description: `success=${successCount}. If nothing appears: minimize Chrome, check macOS Notifications for Google Chrome, and watch DevTools → Console for [FCM SW].`,
          status: 'success',
          position: 'top-right',
          duration: 9000,
        });
      }    } catch (err) {
      toast({
        title: err?.message || 'Test notification failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setTestBusy(false);
    }
  };

  const renderField = (def) => {
    if (!draft || !isConfigFieldVisible(def, draft)) return null;

    if (def.type === 'boolean') {
      return (
        <Flex
          key={def.key}
          p={4}
          bg="bg.input"
          borderRadius="sm"
          borderWidth="1px"
          borderColor="border.subtle"
          align="center"
          justify="space-between"
          gap={4}
          h="100%"
        >
          <Box minW={0}>
            <Text fontWeight="600" fontSize="14px">
              {def.label}
            </Text>
            <Text fontSize="12px" color="text.secondary" mt={1}>
              {def.description}
            </Text>
          </Box>
          <Switch
            colorScheme="yellow"
            isChecked={Boolean(draft[def.key])}
            isDisabled={!canEdit || saving}
            onChange={(e) => patch(def.key, e.target.checked)}
          />
        </Flex>
      );
    }

    if (def.type === 'text') {
      return (
        <Box key={def.key}>
          <AppTextarea
            label={def.label}
            isRequired={Boolean(def.requiredWhen)}
            value={draft[def.key]}
            isReadOnly={!canEdit}
            onChange={(e) => patch(def.key, e.target.value)}
            rows={3}
            maxLength={def.maxLength}
            isInvalid={!!errors[def.key]}
            errorMessage={errors[def.key]}
            helperText={def.description}
          />
        </Box>
      );
    }

    return (
      <AppInput
        key={def.key}
        label={def.label}
        value={draft[def.key]}
        isReadOnly={!canEdit}
        inputMode="numeric"
        onChange={(e) =>
          patch(def.key, e.target.value.replace(/\D/g, '').slice(0, 4))
        }
        isInvalid={!!errors[def.key]}
        errorMessage={errors[def.key]}
        helperText={`${def.description} (${def.min}–${def.max})`}
      />
    );
  };

  const pushStatusLabel = !pushStatus.configured
    ? 'Firebase web config not set up yet'
    : pushStatus.permission === 'granted' && pushStatus.hasLocalToken
      ? 'Browser push is enabled on this device'
      : pushStatus.permission === 'denied'
        ? 'Notifications blocked in the browser'
        : 'Browser push not enabled on this device yet';

  if (loading || !draft) {
    return <PageLoader label="Loading system configuration…" />;
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Heading size={{ base: 'lg', xl: 'xl' }}>System configuration</Heading>
          <Text color="text.secondary" mt={2} maxW="720px">
            Business rules and feature toggles used across the app — change them here
            instead of in code.
          </Text>
        </Box>
        {canEdit ? (
          <Flex gap={2} wrap="wrap">
            <OutlineButton
              size="sm"
              leftIcon={<MdRestartAlt />}
              onClick={handleReset}
              isLoading={saving}
            >
              Reset defaults
            </OutlineButton>
            <PrimaryButton
              size="sm"
              leftIcon={<MdSave />}
              onClick={handleSave}
              isLoading={saving}
            >
              Save changes
            </PrimaryButton>
          </Flex>
        ) : null}
      </Flex>

      {!canEdit ? (
        <Box mb={6}>
          <StatusAlertCard
            title="Read only"
            body="You can view these settings but need the System configuration edit right to change them."
          />
        </Box>
      ) : null}

      <VStack align="stretch" spacing={4}>
        {groups.map(([group, defs]) => (
          <AppCard key={group}>
            <Heading size="sm" mb={4}>
              {group}
            </Heading>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
              {defs.map((def) => renderField(def))}
            </SimpleGrid>
          </AppCard>
        ))}

        <AppCard>
          <Heading size="sm" mb={2}>
            Notifications
          </Heading>
          <Text fontSize="13px" color="text.secondary" mb={3}>
            Staff browser push (web). New indent requests notify registered staff
            browsers. Enable notifications on this device, then use Test to verify.
          </Text>
          <Text fontSize="13px" mb={3}>
            Status: {pushStatusLabel}
          </Text>
          <Flex
            gap={3}
            wrap="wrap"
            align={{ base: 'stretch', sm: 'flex-end' }}
          >
            <Box minW={{ base: '100%', sm: '120px' }} maxW="140px">
              <AppInput
                label="Send after (sec)"
                value={testDelaySeconds}
                inputMode="numeric"
                onChange={(e) =>
                  setTestDelaySeconds(
                    e.target.value.replace(/\D/g, '').slice(0, 2)
                  )
                }
                helperText="0 = send now (max 60)"
              />
            </Box>
            <Flex gap={2} wrap="wrap" align="center" pb={{ sm: '2px' }}>
              <OutlineButton
                size="xs"
                h="28px"
                fontSize="12px"
                px={3}
                onClick={handleEnablePush}
                isLoading={pushBusy}
              >
                Enable browser notifications
              </OutlineButton>
              <PrimaryButton
                size="xs"
                h="28px"
                fontSize="12px"
                px={3}
                leftIcon={<MdNotificationsActive size={14} />}
                onClick={handleTestNotification}
                isLoading={testBusy}
              >
                Send test notification
              </PrimaryButton>
            </Flex>
          </Flex>
          {!pushStatus.configured ? (
            <Text fontSize="12px" color="text.secondary" mt={3}>
              Copy <code>public/firebase-config.example.json</code> to{' '}
              <code>public/firebase-config.json</code> and fill values from Firebase
              Console (Web app + Web Push VAPID key).
            </Text>
          ) : null}
        </AppCard>
      </VStack>
    </Box>
  );
}
